(function () {
  // v12: men filter added; duplicate-coordinate store fix; search filters list/markers without moving map; card clicks pan/zoom reliably; search input focus is preserved.
  const filtersBound = new WeakSet();
  const mapsInitialized = new WeakSet();
  const cardClicksBound = new WeakSet();
  let googleMapsLoadingPromise = null;
  let clustererLoadingPromise = null;

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalized(value) {
    const text = String(value || '')
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/İ/g, 'i');

    try {
      return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (error) {
      return text;
    }
  }

  function toNumber(value) {
    if (value === null || value === undefined) return NaN;
    return Number(String(value).replace(',', '.').trim());
  }

  function loadScriptOnce(id, src) {
    const existing = document.getElementById(id);
    if (existing) {
      return Promise.resolve(existing);
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(script);
      script.onerror = () => reject(new Error('Script could not be loaded: ' + src));
      document.head.appendChild(script);
    });
  }

  function loadClusterer() {
    if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
      return Promise.resolve();
    }

    if (!clustererLoadingPromise) {
      clustererLoadingPromise = loadScriptOnce(
        'store-locator-markerclusterer',
        'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js'
      ).catch((error) => {
        console.warn('MarkerClusterer could not be loaded. Markers will still work without clustering.', error);
      });
    }

    return clustererLoadingPromise;
  }

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) {
      return Promise.resolve();
    }

    if (!apiKey) {
      return Promise.resolve();
    }

    if (!googleMapsLoadingPromise) {
      const callbackName = 'storeLocatorGoogleMapsReady_' + Math.random().toString(36).slice(2);
      googleMapsLoadingPromise = new Promise((resolve, reject) => {
        window[callbackName] = function () {
          resolve();
          try {
            delete window[callbackName];
          } catch (error) {
            window[callbackName] = undefined;
          }
        };

        const src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&callback=' + callbackName;
        loadScriptOnce('store-locator-google-maps', src).catch(reject);
      });
    }

    return googleMapsLoadingPromise;
  }

  function containsWholeWord(text, word) {
    return new RegExp('(^|[^a-z0-9])' + word + '([^a-z0-9]|$)', 'i').test(text);
  }

  function isWomenStore(store) {
    const text = normalized([store.title, store.productTypes].join(' '));
    return store.storeType === 'women' ||
      store.storeType === 'both' ||
      containsWholeWord(text, 'women') ||
      text.includes('kadın') ||
      text.includes('kadin');
  }

  function isMenStore(store) {
    const text = normalized([store.title, store.productTypes].join(' '));

    if (store.storeType === 'men' || store.storeType === 'both') return true;
    if (containsWholeWord(text, 'men') || text.includes('erkek')) return true;

    // Current data has many general stores with store_type = all.
    // Treat these as Men stores unless they are explicitly marked/named as Women.
    return store.storeType === 'all' && !isWomenStore(store);
  }

  function getInfoHtml(store) {
    const title = escapeHtml(store.title);
    const address = escapeHtml(store.address);
    const phone = escapeHtml(store.phone);
    const productTypes = escapeHtml(store.productTypes);
    const directions = store.directionsLink
      ? `<a class="store-locator__info-link" href="${escapeHtml(store.directionsLink)}" target="_blank" rel="noopener nofollow">Get directions</a>`
      : '';

    return `
      <div class="store-locator__info">
        ${title ? `<strong class="store-locator__info-title">${title}</strong>` : ''}
        ${address ? `<div>${address}</div>` : ''}
        ${phone ? `<div>${phone}</div>` : ''}
        ${productTypes ? `<span class="store-locator__info-meta">${productTypes}</span>` : ''}
        ${directions}
      </div>
    `;
  }

  function readStores(root) {
    if (root._storeLocatorStores) return root._storeLocatorStores;

    const jsonEl = root.querySelector('.js-store-data');
    if (!jsonEl) return [];

    try {
      root._storeLocatorStores = JSON.parse(jsonEl.textContent).map((store) => {
        const selector = `[data-store-id="${window.CSS && CSS.escape ? CSS.escape(store.id) : String(store.id).replace(/"/g, '\\"')}"]`;
        return {
          ...store,
          lat: toNumber(store.latitude),
          lng: toNumber(store.longitude),
          marker: null,
          card: root.querySelector(selector)
        };
      });
    } catch (error) {
      console.error('Store locator JSON could not be parsed.', error);
      root._storeLocatorStores = [];
    }

    return root._storeLocatorStores;
  }

  function matches(store, query, filter) {
    const cardText = store.card ? store.card.getAttribute('data-search-text') || '' : '';
    const text = normalized([
      cardText,
      store.title,
      store.address,
      store.phone,
      store.country,
      store.productTypes
    ].join(' '));

    const matchesQuery = !query || text.includes(query);
    const matchesFilter = filter === 'all' ||
      (filter === 'men' && isMenStore(store)) ||
      (filter === 'women' && isWomenStore(store));

    return matchesQuery && matchesFilter;
  }

  function setCardVisible(card, visible) {
    if (!card) return;

    // Shopify/theme CSS can sometimes override the native [hidden] display rule.
    // Use inline display with !important so the left list always follows search/filter state.
    if (visible) {
      card.hidden = false;
      card.classList.remove('is-hidden');
      card.style.removeProperty('display');
      card.setAttribute('aria-hidden', 'false');
    } else {
      card.classList.add('is-hidden');
      card.hidden = true;
      card.style.setProperty('display', 'none', 'important');
      card.setAttribute('aria-hidden', 'true');
    }
  }

  function scrollListToTop(root) {
    const list = root.querySelector('.js-store-list');
    if (!list) return;

    list.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function scrollToMapOnMobile(root) {
    if (!window.matchMedia('(max-width: 989px)').matches) return;

    const mapTarget = root.querySelector('.store-locator__map-wrap') || root.querySelector('.store-locator__map');
    if (!mapTarget) return;

    setTimeout(function () {
      const topOffset = 12;
      const targetTop = mapTarget.getBoundingClientRect().top + window.pageYOffset - topOffset;

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      });
    }, 180);
  }

  function setActiveCard(root, activeCard) {
    root.querySelectorAll('.js-store-card.is-active').forEach((card) => {
      card.classList.remove('is-active');
    });

    if (activeCard) {
      activeCard.classList.add('is-active');
      activeCard.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function clearActiveCard(root) {
    root.querySelectorAll('.js-store-card.is-active').forEach((card) => {
      card.classList.remove('is-active');
    });
  }

  function preserveInputFocus(input, callback) {
    const wasFocused = document.activeElement === input;
    const selectionStart = input && typeof input.selectionStart === 'number' ? input.selectionStart : null;
    const selectionEnd = input && typeof input.selectionEnd === 'number' ? input.selectionEnd : null;

    callback();

    if (!wasFocused || !input) return;

    window.requestAnimationFrame(function () {
      try {
        input.focus({ preventScroll: true });
        if (selectionStart !== null && selectionEnd !== null) {
          input.setSelectionRange(selectionStart, selectionEnd);
        }
      } catch (error) {
        input.focus();
      }
    });
  }

  function applyFilters(root, options) {
    options = options || {};
    const stores = readStores(root);
    const queryInput = root.querySelector('.js-store-search');
    const checkedFilter = root.querySelector('input[type="radio"]:checked');
    const emptyEl = root.querySelector('.js-store-empty');
    const query = normalized(queryInput ? queryInput.value.trim() : '');
    const filter = checkedFilter ? checkedFilter.value : 'all';
    const visibleMarkers = [];
    let visibleCards = 0;

    stores.forEach((store) => {
      // Re-find card as a fallback. Some Shopify theme editor reloads can detach/recreate DOM nodes.
      if (!store.card && store.id) {
        store.card = root.querySelector('[data-store-id="' + String(store.id).replace(/"/g, '\"') + '"]');
      }

      const visible = matches(store, query, filter);

      setCardVisible(store.card, visible);

      if (visible) visibleCards += 1;

      if (store.marker) {
        store.marker.setVisible(visible);
        if (visible) visibleMarkers.push(store.marker);
      }
    });

    if (root._storeLocatorClusterer) {
      root._storeLocatorClusterer.clearMarkers();
      root._storeLocatorClusterer.addMarkers(visibleMarkers);
    }

    if (emptyEl) emptyEl.hidden = visibleCards > 0;

    if (options.clearActive) {
      clearActiveCard(root);
    }

    if (options.closeInfoWindow && root._storeLocatorInfoWindow) {
      root._storeLocatorInfoWindow.close();
    }
  }

  function fitToVisibleMarkers(root) {
    const map = root._storeLocatorMap;
    if (!map) return;

    const stores = readStores(root);
    const visibleStores = stores.filter((store) => store.marker && store.marker.getVisible());
    if (!visibleStores.length) return;

    const bounds = new google.maps.LatLngBounds();
    visibleStores.forEach((store) => bounds.extend(store.marker.getPosition()));
    map.fitBounds(bounds);

    if (visibleStores.length === 1) {
      google.maps.event.addListenerOnce(map, 'bounds_changed', function () {
        map.setZoom(Math.min(map.getZoom(), 15));
      });
    }
  }

  function openInfoWindowAtStore(root, store) {
    if (!root._storeLocatorInfoWindow || !root._storeLocatorMap) return;
    if (!Number.isFinite(store.lat) || !Number.isFinite(store.lng)) return;

    const position = { lat: store.lat, lng: store.lng };
    const infoWindow = root._storeLocatorInfoWindow;

    infoWindow.setContent(getInfoHtml(store));
    infoWindow.setPosition(position);

    // Opening by position is more reliable than anchoring to a marker when two stores
    // share the exact same coordinates and MarkerClusterer groups/hides the markers.
    try {
      infoWindow.open({ map: root._storeLocatorMap });
    } catch (error) {
      infoWindow.open(root._storeLocatorMap);
    }
  }

  function focusStoreOnMap(root, store) {
    const map = root._storeLocatorMap;
    if (!map || !store) return;
    if (!Number.isFinite(store.lat) || !Number.isFinite(store.lng)) return;

    const position = { lat: store.lat, lng: store.lng };
    const cardClickZoom = Number(root.dataset.cardClickZoom || 13);

    setActiveCard(root, store.card);

    // Always use the real coordinates from the store data instead of relying on the
    // marker object. This fixes stores with identical coordinates such as Akasya or Çeşme Marina.
    map.setZoom(cardClickZoom);
    map.panTo(position);

    // A tiny delayed setCenter prevents InfoWindow/cluster repainting from pulling
    // the map back to the previous searched location in some browsers.
    window.setTimeout(function () {
      map.setCenter(position);
    }, 80);

    openInfoWindowAtStore(root, store);
    scrollToMapOnMobile(root);
  }

  function bindCardClicks(root) {
    if (cardClicksBound.has(root)) return;
    cardClicksBound.add(root);

    root.addEventListener('click', function (event) {
      const card = event.target.closest('.js-store-card');
      if (!card || !root.contains(card)) return;

      event.preventDefault();

      const storeId = card.getAttribute('data-store-id');
      const stores = readStores(root);
      const store = stores.find(function (item) {
        return String(item.id) === String(storeId);
      });

      if (!store) return;
      focusStoreOnMap(root, store);
    });
  }

  function bindFilters(root) {
    if (filtersBound.has(root)) return;
    filtersBound.add(root);

    const queryInput = root.querySelector('.js-store-search');
    const filterInputs = root.querySelectorAll('input[type="radio"]');

    if (queryInput) {
      queryInput.setAttribute('autocomplete', 'off');

      queryInput.addEventListener('keydown', function (event) {
        // Prevent Enter from submitting/focusing the map while the user searches.
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      });

      ['input', 'search', 'keyup', 'change'].forEach((eventName) => {
        queryInput.addEventListener(eventName, function () {
          preserveInputFocus(queryInput, function () {
            applyFilters(root, {
              clearActive: true,
              closeInfoWindow: true
            });
          });
        });
      });
    }

    filterInputs.forEach((input) => {
      input.addEventListener('change', function () {
        applyFilters(root, {
          clearActive: true,
          closeInfoWindow: true
        });
        scrollListToTop(root);
      });
    });
  }

  function initMap(root) {
    if (mapsInitialized.has(root)) return;
    if (!window.google || !window.google.maps) return;

    const stores = readStores(root);
    const mapEl = root.querySelector('.js-store-map');
    if (!mapEl) return;

    mapsInitialized.add(root);

    const initialLat = toNumber(root.dataset.initialLat);
    const initialLng = toNumber(root.dataset.initialLng);
    const initialZoom = Number(root.dataset.initialZoom || 6);
    const firstStoreWithCoords = stores.find((store) => Number.isFinite(store.lat) && Number.isFinite(store.lng));

    const center = Number.isFinite(initialLat) && Number.isFinite(initialLng)
      ? { lat: initialLat, lng: initialLng }
      : firstStoreWithCoords
        ? { lat: firstStoreWithCoords.lat, lng: firstStoreWithCoords.lng }
        : { lat: 41.0082, lng: 28.9784 };

    const map = new google.maps.Map(mapEl, {
      center,
      zoom: initialZoom,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.LEFT_TOP
      },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      },
      gestureHandling: 'greedy'
    });

    root._storeLocatorMap = map;

    const infoWindow = new google.maps.InfoWindow();
    root._storeLocatorInfoWindow = infoWindow;
    const markers = [];

    stores.forEach((store) => {
      if (store.showOnMap === false || !Number.isFinite(store.lat) || !Number.isFinite(store.lng)) return;

      const marker = new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        title: store.title || '',
        map
      });

      marker.addListener('click', function () {
        setActiveCard(root, store.card);
        openInfoWindowAtStore(root, store);
      });

      store.marker = marker;
      markers.push(marker);
    });

    if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
      root._storeLocatorClusterer = new window.markerClusterer.MarkerClusterer({ map, markers });
    }


    applyFilters(root);
  }

  function initRoot(root) {
    bindFilters(root);
    bindCardClicks(root);
    applyFilters(root);

    if (!root.dataset.googleApiKey) {
      return;
    }

    Promise.all([
      loadClusterer(),
      loadGoogleMaps(root.dataset.googleApiKey)
    ]).then(() => {
      initMap(root);
    }).catch((error) => {
      console.error('Store locator map could not be initialized.', error);
    });
  }

  window.initAllStoreLocators = function () {
    document.querySelectorAll('.js-store-locator').forEach(initRoot);
  };

  document.addEventListener('DOMContentLoaded', window.initAllStoreLocators);
  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) {
      event.target.querySelectorAll('.js-store-locator').forEach(initRoot);
    }
  });

  if (document.readyState !== 'loading') {
    window.initAllStoreLocators();
  }
})();
